import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConsultationForm = ({ services }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    serviceType: '',
    projectTimeline: '',
    budget: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    { value: '', label: 'Select a service...' },
    ...services.map(service => ({
      value: service.id,
      label: service.title
    }))
  ];

  const timelineOptions = [
    { value: '', label: 'Select timeline...' },
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: '1-3months', label: '1-3 months' },
    { value: '3-6months', label: '3-6 months' },
    { value: '6months+', label: '6+ months' },
    { value: 'exploring', label: 'Just exploring options' }
  ];

  const budgetOptions = [
    { value: '', label: 'Select budget range...' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k-250k', label: '$100,000 - $250,000' },
    { value: '250k+', label: '$250,000+' },
    { value: 'discuss', label: 'Prefer to discuss' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.serviceType) newErrors.serviceType = 'Please select a service';
    if (!formData.projectTimeline) newErrors.projectTimeline = 'Please select a timeline';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      serviceType: '',
      projectTimeline: '',
      budget: '',
      message: ''
    });
    
    setIsSubmitting(false);
    alert('Thank you! We\'ll contact you within 24 hours to discuss your project.');
  };

  return (
    <div className="bg-white rounded-xl border border-border p-6 sticky top-24">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
          <Icon name="MessageSquare" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Request Consultation</h3>
          <p className="text-sm text-muted-foreground">Get expert advice for your project</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={errors.name}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="your.email@company.com"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={errors.email}
          required
        />

        <Input
          label="Company Name"
          type="text"
          placeholder="Your company name"
          value={formData.company}
          onChange={(e) => handleInputChange('company', e.target.value)}
          error={errors.company}
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          description="Optional - for faster communication"
        />

        <Select
          label="Service of Interest"
          options={serviceOptions}
          value={formData.serviceType}
          onChange={(value) => handleInputChange('serviceType', value)}
          error={errors.serviceType}
          required
        />

        <Select
          label="Project Timeline"
          options={timelineOptions}
          value={formData.projectTimeline}
          onChange={(value) => handleInputChange('projectTimeline', value)}
          error={errors.projectTimeline}
          required
        />

        <Select
          label="Budget Range"
          options={budgetOptions}
          value={formData.budget}
          onChange={(value) => handleInputChange('budget', value)}
          description="Helps us tailor our proposal"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Project Details
          </label>
          <textarea
            placeholder="Tell us about your project goals, challenges, and requirements..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending Request...' : 'Request Consultation'}
        </Button>

        <div className="text-xs text-muted-foreground text-center pt-2">
          <Icon name="Shield" size={12} className="inline mr-1" />
          Your information is secure and will never be shared
        </div>
      </form>
    </div>
  );
};

export default ConsultationForm;